import { useEffect, useMemo, useState } from "react"

import { UserProfile } from "@lib/clients/nelo-api-client"
import NeloIcon from "@modules/common/icons/nelo-icon"
import WhatsAppLogo from "@modules/common/icons/whatsapp-icon"
import StepWrapper from "@modules/signup-and-order-flow/components/common/StepWrapper"

import VerificationItem from "./VerificationItem"

type WhatsappIdentityVerificationProps = {
  productTitle?: string
  sendWhatsappLink: (productTitle?: string) => void
  downloadApp: () => void
  handleCloseSheet: () => void
  userProfile?: UserProfile
}

const VERIFICATION_TEXTS = {
  title: "Para finalizar, verifica tu identidad",
  subtitle: "",
  whatsappText: "Haz clic en el link que enviamos vía WhatsApp.",
  whatsappRetryText: "Reenviar link",
  downloadAppText: "Descarga la app para continuar",
  continueButtonText: "Descarga la app",
}

const WhatsappIdentityVerification = ({
  sendWhatsappLink,
  downloadApp,
  handleCloseSheet,
  productTitle,
  userProfile,
}: WhatsappIdentityVerificationProps) => {
  const [isClickable, setIsClickable] = useState(false)
  const [timer, setTimer] = useState(30)

  const phoneNumber = userProfile?.phoneNumber
  const whatsappText = phoneNumber ? `Haz clic en el link que enviamos al ${phoneNumber} vía WhatsApp.` : VERIFICATION_TEXTS.whatsappText

  useEffect(() => {
    sendWhatsappLink(productTitle)
  }, [])

  useEffect(() => {
    if (timer === 0) {
      setIsClickable(true)
      return
    }

    const countdown = setInterval(() => {
      setTimer((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(countdown)
  }, [timer])

  const handleClick = () => {
    sendWhatsappLink(productTitle)
    setIsClickable(false)
    setTimer(30)
  }

  const resendLinkButton = useMemo(
    () => (
      <button
        type="button"
        onClick={handleClick}
        disabled={!isClickable}
        className={`self-start text-sm ${isClickable ? "text-nelo-blue hover:underline" : "cursor-not-allowed text-gray-400"}`}
      >
        Reenviar link {isClickable ? "" : `(${timer}s)`}
      </button>
    ),
    [isClickable, timer, handleClick]
  )

  return (
    <StepWrapper
      title={VERIFICATION_TEXTS.title}
      subtitle={VERIFICATION_TEXTS.subtitle}
      onBack={handleCloseSheet}
      progressPercentage={100}
      onSubmit={downloadApp}
      showBackButton={true}
      submitButtonText={VERIFICATION_TEXTS.continueButtonText}
    >
      <div className="mb-11">
        <VerificationItem icon={<WhatsAppLogo />} description={whatsappText} actionComponent={resendLinkButton} />

        <VerificationItem
          icon={<NeloIcon />}
          description="Descarga la app Nelo y verifica tu identidad desde tu cuenta."
          className="mt-4"
        />
      </div>
    </StepWrapper>
  )
}

export default WhatsappIdentityVerification
